<template>
  <b-card
    aria-label="Formulaire de création de facture fournisseur"
    role="form"
  >
    <h4 class="mb-3">
      Générer une Facture Fournisseur
    </h4>
    <p class="text-muted mb-4">
      Créez une facture basée sur une période spécifique. Le système calculera automatiquement le montant en fonction des commandes livrées.
    </p>

    <div
      v-if="isLoading === true"
      class="text-center mb-2"
    >
      <b-spinner
        variant="primary"
        label="Chargement..."
      />
    </div>

    <b-form
      v-if="!isLoading"
      @submit.prevent="createInvoice"
    >
      <!-- Selection Mode -->
      <b-row class="mb-3">
        <b-col cols="12">
          <b-form-group label="Mode de sélection">
            <b-form-radio-group
              v-model="selectionMode"
              :options="[
                { text: 'Sélection rapide par mois', value: 'month' },
                { text: 'Période personnalisée', value: 'custom' }
              ]"
              button-variant="outline-primary"
              buttons
              @change="onModeChange"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Month Selection Mode -->
      <b-row v-if="selectionMode === 'month'">
        <b-col md="6">
          <b-form-group
            label="Mois"
            label-for="month"
            description="Sélectionnez le mois de facturation."
          >
            <b-form-select
              id="month"
              v-model="selectedMonth"
              :options="monthOptions"
              @change="updateDatesFromMonth"
            />
          </b-form-group>
        </b-col>

        <b-col md="6">
          <b-form-group
            label="Année"
            label-for="year"
            description="Sélectionnez l'année de facturation."
          >
            <b-form-select
              id="year"
              v-model="selectedYear"
              :options="yearOptions"
              @change="updateDatesFromMonth"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Custom Date Range Mode -->
      <b-row v-if="selectionMode === 'custom'">
        <b-col md="6">
          <b-form-group
            label="Date de début"
            label-for="startDate"
            aria-label="Date de début de la période"
            description="Sélectionnez la date de début de la période de facturation."
          >
            <b-form-input
              id="startDate"
              v-model="startDate"
              type="date"
              required
            />
          </b-form-group>
        </b-col>

        <b-col md="6">
          <b-form-group
            label="Date de fin"
            label-for="endDate"
            aria-label="Date de fin de la période"
            description="Sélectionnez la date de fin de la période de facturation."
          >
            <b-form-input
              id="endDate"
              v-model="endDate"
              type="date"
              required
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Display selected period -->
      <b-row v-if="startDate && endDate">
        <b-col cols="12">
          <b-alert
            show
            variant="info"
            class="mb-3"
          >
            <div class="d-flex align-items-center">
              <feather-icon
                icon="CalendarIcon"
                class="mr-2"
              />
              <div>
                <strong>Période sélectionnée:</strong> {{ formatDate(startDate) }} - {{ formatDate(endDate) }}
              </div>
            </div>
          </b-alert>
        </b-col>
      </b-row>

      <!-- Pricing Mode -->
      <b-row class="mb-3">
        <b-col cols="12">
          <b-form-group label="Mode de tarification">
            <b-form-radio-group
              v-model="useTiered"
              :options="[
                { text: 'Plan par Paliers (automatique)', value: true },
                { text: 'Tarif fixe (manuel)', value: false }
              ]"
              button-variant="outline-primary"
              buttons
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Tiered info banner -->
      <b-row v-if="useTiered">
        <b-col cols="12">
          <b-alert
            show
            variant="success"
            class="mb-3"
          >
            <div class="d-flex align-items-start">
              <feather-icon
                icon="TrendingUpIcon"
                class="mr-2 mt-1"
              />
              <div>
                <strong>Plan par Paliers activé</strong> — le prix par commande sera calculé automatiquement selon le volume livré :
                <ul class="mt-1 mb-0">
                  <li>Micro (0–999) : 0.120 TND/cmd</li>
                  <li>Small (1 000–3 000) : 0.100 TND/cmd</li>
                  <li>Growth (3 001–7 500) : 0.060 TND/cmd</li>
                  <li>Standard (7 501–25 000) : 0.040 TND/cmd</li>
                  <li>High Volume (25 001–30 000) : 0.035 TND/cmd</li>
                  <li>Entreprise (30 001+) : prix négocié — à saisir manuellement</li>
                </ul>
              </div>
            </div>
          </b-alert>
        </b-col>
      </b-row>

      <!-- Flat rate inputs (only in manual mode) -->
      <b-row v-if="!useTiered">
        <b-col md="6">
          <b-form-group
            label="Prix par commande sous-traitance (TND)"
            label-for="outsourcingPrice"
            aria-label="Prix unitaire pour les commandes sous-traitées"
            description="Prix appliqué aux commandes livrées par des sociétés de sous-traitance."
          >
            <b-form-input
              id="outsourcingPrice"
              v-model="outsourcingPrice"
              type="number"
              step="0.001"
              placeholder="0.150"
            />
          </b-form-group>
        </b-col>

        <b-col md="6">
          <b-form-group
            label="Prix par commande directe (TND)"
            label-for="companyPrice"
            aria-label="Prix unitaire pour les commandes directes"
            description="Prix appliqué aux commandes livrées directement par l'entreprise."
          >
            <b-form-input
              id="companyPrice"
              v-model="companyPrice"
              type="number"
              step="0.001"
              placeholder="0.200"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-button
        type="submit"
        variant="success"
        :disabled="!startDate || !endDate"
        class="mt-2"
      >
        <feather-icon
          icon="PlusIcon"
          class="mr-1"
        />
        Générer la Facture
      </b-button>

      <b-alert
        v-model="alertVisible"
        :show="alertVisible"
        :variant="alertVariant"
        dismissible
        class="mt-3"
      >
        {{ alertMessage }}
      </b-alert>
    </b-form>
  </b-card>
</template>

<script>
import {
  BCard, BForm, BFormGroup, BFormInput, BButton, BAlert, BRow, BCol, BSpinner,
  BFormSelect, BFormRadioGroup,
} from 'bootstrap-vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'CreateInvoiceProvider',
  components: {
    BCard,
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    BAlert,
    BRow,
    BCol,
    BSpinner,
    BFormSelect,
    BFormRadioGroup,
  },
  data() {
    const currentDate = new Date()
    const currentYear = currentDate.getFullYear()
    const currentMonth = currentDate.getMonth() + 1 // 0-indexed

    return {
      selectionMode: 'month', // 'month' or 'custom'
      selectedMonth: currentMonth,
      selectedYear: currentYear,
      startDate: '',
      endDate: '',
      useTiered: true,
      outsourcingPrice: 0.150,
      companyPrice: 0.200,
      isLoading: false,
      alertVisible: false,
      alertVariant: '',
      alertMessage: '',
      monthOptions: [
        { value: 1, text: 'Janvier' },
        { value: 2, text: 'Février' },
        { value: 3, text: 'Mars' },
        { value: 4, text: 'Avril' },
        { value: 5, text: 'Mai' },
        { value: 6, text: 'Juin' },
        { value: 7, text: 'Juillet' },
        { value: 8, text: 'Août' },
        { value: 9, text: 'Septembre' },
        { value: 10, text: 'Octobre' },
        { value: 11, text: 'Novembre' },
        { value: 12, text: 'Décembre' },
      ],
      yearOptions: this.generateYearOptions(),
    }
  },
  mounted() {
    // Set initial dates based on current month
    this.updateDatesFromMonth()
  },
  methods: {
    async createInvoice() {
      if (!this.startDate || !this.endDate) {
        this.showAlert('danger', 'Veuillez remplir les dates de début et de fin.')
        return
      }

      // Validate date range
      if (new Date(this.startDate) > new Date(this.endDate)) {
        this.showAlert('danger', 'La date de début doit être antérieure à la date de fin.')
        return
      }

      this.isLoading = true
      try {
        const invoiceData = {
          start_date: this.startDate,
          end_date: this.endDate,
          use_tiered: this.useTiered,
          ...(!this.useTiered && {
            outsourcing_price_per_order: parseFloat(this.outsourcingPrice) || 0.150,
            company_price_per_order: parseFloat(this.companyPrice) || 0.200,
          }),
        }

        await axios.post('/api/invoices/invoice-providers/generate_custom_invoice/', invoiceData)

        this.showToast('success', 'Facture générée avec succès!')
        this.resetForm()

        // Redirect to list after a short delay
        setTimeout(() => {
          this.$router.push({ name: 'invoice-list-direx' })
        }, 1500)
      } catch (error) {
        console.error('Error creating invoice:', error)
        const errorMsg = error.response?.data?.error || 'Erreur lors de la création de la facture.'
        this.showAlert('danger', errorMsg)
        this.showToast('danger', errorMsg)
      } finally {
        this.isLoading = false
      }
    },
    resetForm() {
      const currentDate = new Date()
      this.selectedMonth = currentDate.getMonth() + 1
      this.selectedYear = currentDate.getFullYear()
      this.startDate = ''
      this.endDate = ''
      this.useTiered = true
      this.outsourcingPrice = 0.150
      this.companyPrice = 0.200
      if (this.selectionMode === 'month') {
        this.updateDatesFromMonth()
      }
    },
    generateYearOptions() {
      const currentYear = new Date().getFullYear()
      const years = []
      // Generate years from 2020 to current year + 1
      for (let year = 2020; year <= currentYear + 1; year += 1) {
        years.push({ value: year, text: year.toString() })
      }
      return years
    },
    updateDatesFromMonth() {
      if (!this.selectedMonth || !this.selectedYear) return

      // Get first day of month
      const firstDay = new Date(this.selectedYear, this.selectedMonth - 1, 1)
      // Get last day of month
      const lastDay = new Date(this.selectedYear, this.selectedMonth, 0)

      // Format dates as YYYY-MM-DD
      this.startDate = this.formatDateForInput(firstDay)
      this.endDate = this.formatDateForInput(lastDay)
    },
    formatDateForInput(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      })
    },
    onModeChange() {
      // When switching modes, update dates if in month mode
      if (this.selectionMode === 'month') {
        this.updateDatesFromMonth()
      }
    },
    showAlert(variant, message) {
      this.alertVariant = variant
      this.alertMessage = message
      this.alertVisible = true
    },
    showToast(variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: variant === 'success' ? 'Succès' : 'Erreur',
          icon: variant === 'success' ? 'CheckCircleIcon' : 'AlertCircleIcon',
          text,
          variant,
        },
      })
    },
  },
}
</script>

<style scoped>
.b-form-group {
  margin-bottom: 1.5rem;
}

/* Radio button group styling */
::v-deep .btn-group-toggle .btn {
  padding: 0.75rem 1.5rem;
  font-weight: 500;
}

::v-deep .btn-group-toggle .btn.active {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Alert styling */
.alert {
  border-radius: 0.5rem;
}

/* Form select styling */
select.form-control {
  cursor: pointer;
}
</style>
